import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { usersAPI } from '../../services/api';
import { reconcile } from './moduleRegistry';

const storageKey = (userId) => `ga_dashboard_layout_${userId}`;

function readCache(userId) {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

// In-flight fetch dedupe keyed by userId. Collapses the multiple loads that
// otherwise fire per dashboard mount (StrictMode double-invoke, effect re-runs
// as auth/facility state hydrates, multiple hook consumers) into a single
// request (GA-006).
const inflight = new Map();

function loadLayoutOnce(userId) {
  let promise = inflight.get(userId);
  if (!promise) {
    promise = usersAPI
      .getDashboardLayout(userId)
      .then((response) => response?.data ?? null)
      .catch(() => readCache(userId))
      .finally(() => inflight.delete(userId));
    inflight.set(userId, promise);
  }
  return promise;
}

/**
 * Per-user dashboard module layout: { order: string[], hidden: string[] }.
 *
 * Load: server first (deduped, once per user), localStorage fallback
 * (offline/PWA), role default last. A missing server layout (404) is a normal
 * default-layout outcome, not an error. Reconciliation against the current role
 * is decoupled from the fetch so role/permission identity changes never trigger
 * a refetch.
 *
 * Save: optimistic local state + localStorage immediately, server write
 * debounced so rapid drag operations collapse into one PUT.
 */
export function useDashboardLayout(userId, roleName, hasPermission) {
  const [rawLayout, setRawLayout] = useState(() => (userId ? readCache(userId) : null));
  const fetchedUserRef = useRef(null);
  const saveTimer = useRef(null);

  useEffect(() => {
    if (!userId) return undefined;
    // Only fetch once per user; role/permission changes must not refetch.
    if (fetchedUserRef.current === userId) return undefined;
    fetchedUserRef.current = userId;

    let cancelled = false;
    loadLayoutOnce(userId).then((saved) => {
      if (!cancelled) setRawLayout(saved ?? null);
    });

    return () => {
      cancelled = true;
    };
  }, [userId]);

  // Reconcile the raw (server/cache) layout against the live role + permissions.
  const layout = useMemo(
    () => reconcile(rawLayout, roleName, hasPermission),
    [rawLayout, roleName, hasPermission]
  );

  useEffect(() => () => clearTimeout(saveTimer.current), []);

  const update = useCallback(
    (next) => {
      setRawLayout(next);
      try {
        localStorage.setItem(storageKey(userId), JSON.stringify(next));
      } catch {
        // Storage full/unavailable — server save below still applies.
      }
      clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => {
        usersAPI.saveDashboardLayout(userId, next).catch(() => {
          // Offline or transient failure: localStorage copy is the fallback
          // and will reconcile against the server on next load.
        });
      }, 800);
    },
    [userId]
  );

  return useMemo(() => ({ layout, update }), [layout, update]);
}
